import React, { useEffect, useState } from 'react';
import { getAppBillingUrl, getAppPanelLoginUrl } from '../../utils/constants';
import './NewLanding.css';

const ACADEMY_LESSONS = [
  {
    title: 'System, który Cię okrada',
    meta: 'Moduł 1 · Fundamenty rynku',
    progress: 18,
    label: '01',
    name: 'System, który Cię okrada',
  },
  {
    title: 'Jak wykorzystać kryptowaluty',
    meta: 'Moduł 1 · Fundamenty rynku',
    progress: 0,
    label: '02',
    name: 'Jak wykorzystać kryptowaluty',
  },
  {
    title: 'Jak legalnie nie płacić podatków',
    meta: 'Moduł 2 · Podatki i ochrona kapitału',
    progress: 0,
    label: '03',
    name: 'Jak legalnie nie płacić podatków',
  },
  {
    title: 'Impulsy',
    meta: 'Moduł 3 · Analiza na wykresie',
    progress: 0,
    label: '04',
    name: 'Impulsy',
  },
  {
    title: 'Fibo',
    meta: 'Moduł 3 · Analiza na wykresie',
    progress: 0,
    label: '05',
    name: 'Fibo',
  },
  {
    title: 'Strategia Delty',
    meta: 'Moduł 4 · Strategie i portfel',
    progress: 0,
    label: '06',
    name: 'Strategia Delty',
  },
  {
    title: 'Handel Zone Trading',
    meta: 'Moduł 4 · Strategie i portfel',
    progress: 0,
    label: '07',
    name: 'Handel Zone Trading',
  },
] as const;

const LEGAL_BASE = 'https://app.forumkapitalu.pl';

const NewLanding: React.FC = () => {
  const [activeLesson, setActiveLesson] = useState(0);
  const billingUrl = getAppBillingUrl();
  const loginUrl = getAppPanelLoginUrl();
  const lesson = ACADEMY_LESSONS[activeLesson];

  useEffect(() => {
    const root = document.querySelector('.fk-landing');
    if (!root) return undefined;

    const revealEls = root.querySelectorAll<HTMLElement>('[data-reveal]');
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
            observer.unobserve(entry.target);
          }
        }
      },
      {
        root: null,
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.18,
      },
    );
    revealEls.forEach((el) => observer.observe(el));

    const panels = root.querySelectorAll<HTMLElement>('.panel');
    let ticking = false;
    const parallax = () => {
      const vh = window.innerHeight;
      panels.forEach((panel) => {
        const rect = panel.getBoundingClientRect();
        const mid = rect.top + rect.height / 2;
        const progress = (mid - vh / 2) / vh;
        const offset = Math.max(-14, Math.min(14, progress * -18));
        panel.style.translate = `0 ${offset.toFixed(2)}px`;
      });
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        parallax();
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    parallax();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      panels.forEach((panel) => {
        panel.style.translate = '';
      });
    };
  }, []);

  return (
    <div className="fk-landing">
      <div className="ambient" aria-hidden="true">
        <div className="ambient__glow ambient__glow--a" />
        <div className="ambient__glow ambient__glow--b" />
      </div>

      <main>
        <a className="login-btn" href={loginUrl}>
          Zaloguj się
        </a>

        <section className="hero" id="top">
          <div className="hero__stage">
            <div className="hero__brand" aria-label="Forum Kapitału">
              <div className="hero__light" aria-hidden="true" />
              <span className="hero__word hero__word--left" aria-hidden="true">
                Forum
              </span>
              <img
                className="hero__logo"
                src={`${process.env.PUBLIC_URL}/images/logo.svg`}
                alt=""
                width={280}
                height={280}
              />
              <span className="hero__word hero__word--right" aria-hidden="true">
                Kapitału
              </span>
            </div>
          </div>
          <a className="hero__scroll" href="#doswiadczenie" aria-label="Przewiń dalej">
            <span>Czym jest Forum Kapitału</span>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 5v14M5 12l7 7 7-7"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </section>

        <div id="doswiadczenie" className="chapters">
          <section className="chapter" data-reveal>
            <div className="chapter__copy">
              <p className="chapter__index">01</p>
              <h2 className="chapter__title">
                Najpierw uczysz się
                <br />
                czytać rynek
              </h2>
              <p className="chapter__text">
                Akademia z doświadczenia, nie z teorii. Opracowana na podstawie setek godzin z
                mentorami, którzy żyją z rynku
              </p>
            </div>
            <figure className="shot">
              <div className="shot__glow" aria-hidden="true" />
              <div className="panel panel--academy" id="academy-demo">
                <div className="panel__bar">
                  <span className="panel__dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="panel__label">Akademia · prawdziwe lekcje</span>
                </div>
                <div className="academy">
                  <div className="academy__player">
                    <div className="academy__screen">
                      <span className="academy__badge">Wybrane lekcje</span>
                      <p className="academy__now" id="academy-title">
                        {lesson.title}
                      </p>
                      <div className="academy__progress" aria-hidden="true">
                        <span id="academy-progress" style={{ width: `${lesson.progress}%` }} />
                      </div>
                      <p className="academy__meta" id="academy-meta">
                        {lesson.meta}
                      </p>
                      <p className="academy__count">30 lekcji w Akademii</p>
                    </div>
                  </div>
                  <ul className="academy__list" role="list">
                    {ACADEMY_LESSONS.map((item, index) => (
                      <li key={item.label}>
                        <button
                          type="button"
                          className={`academy__lesson${index === activeLesson ? ' is-active' : ''}`}
                          onClick={() => setActiveLesson(index)}
                        >
                          <span>{item.label}</span>
                          <em>{item.name}</em>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </figure>
          </section>

          <section className="chapter chapter--flip" data-reveal>
            <div className="chapter__copy">
              <p className="chapter__index">02</p>
              <h2 className="chapter__title">
                Potem masz nasz
                <br />
                portfel na&nbsp;stole
              </h2>
              <p className="chapter__text">
                Widzisz, jakie pozycje zajmujemy, gdzie kupujemy i gdzie ustawiamy take&nbsp;profity
              </p>
            </div>
            <figure className="shot">
              <div className="shot__glow" aria-hidden="true" />
              <div className="panel">
                <div className="panel__bar">
                  <span className="panel__dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="panel__label">Portfel FK · analizy</span>
                </div>
                <div className="panel__body">
                  <div className="folio">
                    <div className="folio__head">
                      <p className="folio__label">Portfel Forum</p>
                      <span className="folio__tag">portfel FK</span>
                    </div>
                    <ul className="panel__tickers">
                      <li>
                        <div>
                          <strong>NVDA</strong>
                          <span>spółki · Strategia ZT</span>
                        </div>
                        <em className="up">+4,2%</em>
                      </li>
                      <li>
                        <div>
                          <strong>BTC</strong>
                          <span>krypto · Średni termin</span>
                        </div>
                        <em className="up">+2,1%</em>
                      </li>
                      <li>
                        <div>
                          <strong>GOLD</strong>
                          <span>surowce · w obserwacji</span>
                        </div>
                        <em>hold</em>
                      </li>
                      <li>
                        <div>
                          <strong>PKN</strong>
                          <span>spółki · Strategia Delty</span>
                        </div>
                        <em className="down">−0,8%</em>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </figure>
          </section>

          <section className="chapter" data-reveal>
            <div className="chapter__copy">
              <p className="chapter__index">03</p>
              <h2 className="chapter__title">
                Dostajesz narzędzia,
                <br />
                których sami używamy
              </h2>
              <p className="chapter__text">Od razu: boty Grid, Delta i arkusz pozycji</p>
            </div>
            <figure className="shot">
              <div className="shot__glow" aria-hidden="true" />
              <div className="panel">
                <div className="panel__bar">
                  <span className="panel__dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="panel__label">Narzędzia</span>
                </div>
                <div className="panel__body panel__body--tools">
                  <div className="tool">
                    <span className="tool__status on" />
                    <div>
                      <strong>Bot Grid</strong>
                      <span>strategia grid · live</span>
                    </div>
                  </div>
                  <div className="tool">
                    <span className="tool__status on" />
                    <div>
                      <strong>Bot Delta</strong>
                      <span>strategia delta · live</span>
                    </div>
                  </div>
                  <div className="tool">
                    <span className="tool__status on" />
                    <div>
                      <strong>Arkusz pozycji</strong>
                      <span>automatycznie pod Twoje entry</span>
                    </div>
                  </div>
                  <div className="sheet" aria-hidden="true">
                    <div className="sheet__row sheet__row--head">
                      <span>Ticker</span>
                      <span>Size</span>
                      <span>PnL</span>
                    </div>
                    <div className="sheet__row">
                      <span>ETH</span>
                      <span>0,8</span>
                      <span className="up">+3,1%</span>
                    </div>
                    <div className="sheet__row">
                      <span>SOL</span>
                      <span>12</span>
                      <span className="up">+1,4%</span>
                    </div>
                    <div className="sheet__row">
                      <span>EURUSD</span>
                      <span>1,0</span>
                      <span className="down">−0,3%</span>
                    </div>
                  </div>
                </div>
              </div>
            </figure>
          </section>

          <section className="chapter chapter--flip" data-reveal>
            <div className="chapter__copy">
              <p className="chapter__index">04</p>
              <h2 className="chapter__title">Zyskujesz drużynę</h2>
              <p className="chapter__text">
                Zamiast działać samemu, jesteś w grupie osób takich jak&nbsp;Ty. Otrzymujesz
                zaproszenia na spotkania w Warszawie, Krakowie czy innych miastach w Polsce
              </p>
            </div>
            <figure className="shot">
              <div className="shot__glow" aria-hidden="true" />
              <div className="panel">
                <div className="panel__bar">
                  <span className="panel__dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="panel__label">Społeczność</span>
                </div>
                <div className="panel__body panel__body--chat">
                  <div className="msg">
                    <span className="msg__who">Łukasz</span>
                    <p>Sprawdźcie nowe zagranie na Tesli, mocne strefy popytowe</p>
                  </div>
                  <div className="msg">
                    <span className="msg__who">Tomek</span>
                    <p>Dobry moment na Google, osobiście zajmuję pozycję</p>
                  </div>
                  <div className="msg">
                    <span className="msg__who">Hubert</span>
                    <p>Wrzucam zdjęcia z meetupu w Warszawie...</p>
                  </div>
                </div>
              </div>
            </figure>
          </section>

          <section className="chapter" data-reveal>
            <div className="chapter__copy">
              <p className="chapter__index">05</p>
              <h2 className="chapter__title">
                Forum, w&nbsp;którym
                <br />
                nie opłaca się nie&nbsp;być
              </h2>
              <p className="chapter__text">
                100&nbsp;zł na rok. Dostęp od razu i 14 dni na zwrot, jeżeli uznasz, że tu nie
                pasujesz
              </p>
            </div>
            <figure className="shot">
              <div className="shot__glow" aria-hidden="true" />
              <div className="panel panel--membership">
                <div className="panel__bar">
                  <span className="panel__dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="panel__label">Członkostwo</span>
                </div>
                <div className="panel__body panel__body--mem">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/logo.svg`}
                    alt=""
                    width={48}
                    height={48}
                  />
                  <p className="mem__name">Forum Kapitału</p>
                  <p className="mem__price">
                    <strong>100&nbsp;zł</strong> / rok
                  </p>
                  <ul className="mem__list">
                    <li>Akademia i czytanie rynku</li>
                    <li>Portfel + codzienne analizy</li>
                    <li>Boty Grid / Delta</li>
                    <li>Społeczność i spotkania</li>
                  </ul>
                </div>
              </div>
            </figure>
          </section>
        </div>

        <section className="finale" data-reveal>
          <div className="finale__light" aria-hidden="true" />
          <img
            className="finale__mark"
            src={`${process.env.PUBLIC_URL}/images/logo.svg`}
            alt=""
            width={72}
            height={72}
          />
          <h2 className="finale__title">
            Codziennie wiemy,
            <br />
            w&nbsp;co inwestować
          </h2>
          <a className="cta" id="checkout" href={billingUrl}>
            <span>A ty?</span>
            <svg className="cta__arrow" viewBox="0 0 56 24" aria-hidden="true">
              <path
                d="M2 12h48M40 5l10 7-10 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>

          <a className="finale__side finale__side--left" href={`${LEGAL_BASE}/regulamin`}>
            Regulamin
          </a>
          <a
            className="finale__side finale__side--right"
            href={`${LEGAL_BASE}/polityka-prywatnosci`}
          >
            Polityka prywatności
          </a>
        </section>
      </main>
    </div>
  );
};

export default NewLanding;
